/** @format */

/* 
    eslint 
    @typescript-eslint/prefer-nullish-coalescing: "warn", 
    @typescript-eslint/prefer-string-starts-ends-with: "warn", 
    @typescript-eslint/no-base-to-string: "warn", max-lines: "off", 
    @typescript-eslint/non-nullable-type-assertion-style: "warn" ,
    @typescript-eslint/dot-notation: "warn"
*/
import isPropValid from '@emotion/is-prop-valid';
import { Divider } from '@mpb-com/feather/components/atoms/divider';
import fastDeepEqual from 'fast-deep-equal/es6/react';
import { Trans, useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import React, {
    Fragment,
    FunctionComponent,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import styled from 'styled-components';

import { RecommendationsVariant } from '@toucan/common/services/feature-flags/constants';
import { SearchFacet } from '@toucan/common/services/search/constants';
import {
    Facet,
    FilterQuery,
    Query,
    QueryResponse,
    SearchListingModel,
    SearchListingProduct,
    SearchValue,
    SortBy,
} from '@toucan/common/services/search/types';

import { toCamelCase, toSnakeCase } from '@toucan/client/global/axios';
import {
    centToDecimal,
    formatAsCurrency,
} from '@toucan/client/global/currency';
import { MarketContext } from '@toucan/client/global/market';
import {
    NextRoute,
    getCurrentRoute,
    getParams,
    setSearchParams,
} from '@toucan/client/global/nextjs';

import { ModelRecommendationsFlag } from '@toucan/client/core/recommendations';
import { scrollToTop } from '@toucan/client/core/scroll';
import {
    FacetValue,
    getListingResults,
    getProductSKUQuery,
    getStarRatingDisplayName,
    getTranslatedFacetTitles,
} from '@toucan/client/core/search';

import { ConstrainedWrapper } from '@toucan/client/shared/components/constrained-wrapper';
import { Link } from '@toucan/client/shared/components/link';
import { useMounted } from '@toucan/client/shared/hooks/use-mounted';

import { ControlGroup } from '@toucan/client/features/search/components/control-group';
import { ControlGroupSkeleton } from '@toucan/client/features/search/components/control-group/skeleton';
import { FilterResultsSummary } from '@toucan/client/features/search/components/filter-results-summary';
import { useSimilarModelRecommendations } from '@toucan/client/features/search/components/listing-page/hooks/use-similar-model-recommendations';
import { useContentfulTiles } from '@toucan/client/features/search/components/listing-page/use-contentful-tiles';
import { SearchFilterGroup } from '@toucan/client/features/search/components/search-filter-group';
import { SearchFilterGroupMobile } from '@toucan/client/features/search/components/search-filter-group-mobile';
import { SearchFilterGroupSkeleton } from '@toucan/client/features/search/components/search-filter-group/skeleton';
import {
    PaginationVariant,
    SearchResults,
    SearchResultsVariant,
} from '@toucan/client/features/search/components/search-results';
import {
    getListingQuerySortBy,
    getModelIdFromQuery,
    getNumRows,
    transformFacetsOptionValue,
    transformFiltersToSearchParams,
    transformSortByToSortByOption,
    trimFilterOptions,
} from '@toucan/client/features/search/helpers';
import { modelListingHelper } from '@toucan/client/features/search/hooks/model-listing';
import { productListingHelper } from '@toucan/client/features/search/hooks/product-listing';
import { useSortByOptions } from '@toucan/client/features/search/hooks/use-sort-by-options';

import { ModelRecommendationsCarousel } from '../..';
import { Filter, FilterOption, SortByOption } from '../../config/types';
import { addSearchResultsToDataLayer } from '../../helpers/analytics';
import { usePriceModifierGroups } from '../../hooks/use-price-modifier-groups';
import { useSearchResultsInfiniteScroll } from '../../hooks/use-search-results-infinite-scroll';
import { ModelRecommendationsCarouselSkeleton } from './components/model-recommendations-carousel/skeleton';
import { DEFAULT_TEST_ID } from './constants';
import {
    formatSearchFacetNumber,
    getNumFilterSkeletons,
    shouldShowFacetCount,
    validateBaseQuery,
} from './helpers';
import { styles } from './listing-page.styles';

type FacetOptionTransformMap = {
    [key in SearchFacet]?: (value: string) => string;
};

export type SearchResultsType<T = SearchListingProduct | SearchListingModel> =
    Omit<
        QueryResponse<T> & {
            sort: SortBy;
        },
        'facets'
    >;

export enum ListingPageVariant {
    MODEL = 'model',
    PRODUCT = 'product',
}

export enum ListingPageType {
    CATEGORY = 'category',
    BRAND = 'brand',
    MODEL = 'model',
    FACETED = 'faceted',
    SEARCH = 'search',
}

export type ListingPageProps = {
    baseQuery: Partial<Query>;
    variant: ListingPageVariant;
    type: ListingPageType;
    onCreateAlert?: () => void;
    resultsPerPage?: number;
    showProductNames?: boolean;
    disallowedFilters?: string[];
    getDefaultSortOption?: (
        options: SortByOption[]
    ) => SortByOption | undefined;
    paginationVariant?: PaginationVariant;
    'data-testid'?: string;
    className?: string;
    isLoading?: boolean;
    isAutoOutOfStockSearchEnabled?: boolean;
    outOfStockRecommendationsFlag?: ModelRecommendationsFlag;
};

export type StyledListingPageProps = Pick<ListingPageProps, 'isLoading'>;

const StyledListingPage = styled('div').withConfig({
    shouldForwardProp: (prop) => isPropValid(prop),
})`
    ${styles}
`;

export const ListingPage: FunctionComponent<ListingPageProps> = ({
    baseQuery = {},
    variant,
    resultsPerPage = 52,
    onCreateAlert,
    showProductNames = false,
    getDefaultSortOption,
    paginationVariant,
    type,
    isLoading = false,
    'data-testid': testId = DEFAULT_TEST_ID,
    isAutoOutOfStockSearchEnabled = false,
    outOfStockRecommendationsFlag,
    ...otherProps
}: ListingPageProps) => {
    const disallowedFilters = [
        'modelType', // This filter is obsolete and no longer used but is still returned from the search service
        ...(otherProps.disallowedFilters ?? []),
    ];
    const modelId = getModelIdFromQuery(baseQuery.filterQuery ?? {});
    const isModelListingPage = variant === ListingPageVariant.MODEL;
    const isProductListingPage = variant === ListingPageVariant.PRODUCT;
    const searchResultsVariant =
        variant === ListingPageVariant.MODEL
            ? SearchResultsVariant.MODEL
            : SearchResultsVariant.PRODUCT;

    const { activeMarket } = useContext(MarketContext);

    const { t } = useTranslation();
    const router = useRouter();
    const isMounted = useMounted();
    const { data: modifierGroups } = usePriceModifierGroups(
        modelId,
        activeMarket.code,
        {
            enabled: isProductListingPage,
        }
    );

    const [mobileSearchFilterOpen, setSearchFilterGroupMobileOpen] =
        useState<boolean>(false);

    const nullSearchResults = {
        results: [],
        resultCount: 0,
        totalResults: -1,
        spellcheck: {
            suggestions: [],
        },
        sort: {},
    };

    const [searchResults, setSearchResults] =
        useState<SearchResultsType>(nullSearchResults);

    const [facets, setFacets] = useState<Facet[]>([]);
    const [facetsFetched, setFacetsFetched] = useState<boolean>(false);

    // Store inflight facet
    const [activeFacetData, setActiveFacetData] = useState<Facet>();

    // Store a reference to translated facet labels..
    const translatedFacetTitles = useMemo(() => {
        return getTranslatedFacetTitles(t);
    }, []);

    // Store a reference to star rating display names..
    const translatedStarRatings = useMemo(() => {
        return getStarRatingDisplayName(t);
    }, []);

    const inStockModelsURL = useRef<NextRoute>();

    const formatPrice = useMemo(() => {
        return formatAsCurrency(activeMarket.iso, activeMarket.currency);
    }, [activeMarket.iso, activeMarket.currency]);

    const selectedPaginationVariant =
        paginationVariant ??
        (isModelListingPage
            ? PaginationVariant.PAGE_SELECT
            : PaginationVariant.VIEW_MORE);

    const isViewMore =
        selectedPaginationVariant === PaginationVariant.VIEW_MORE;

    // How many result skeletons to show
    const [numResultSkeletons, setNumResultSkeletons] = useState<number>(
        getNumRows({
            page: 0,
            lastFetchedPage: 0,
            totalResults: 0,
            resultsPerPage,
            isViewMore: false,
        })
    );

    const searchParams = useMemo(() => {
        const { search: queryParts } = getParams(router);
        const page = queryParts.page ? parseInt(queryParts.page.toString()) : 1;
        const pageIndex = page - 1;
        const showAll = !!queryParts.showAll;
        const filterQuery = queryParts.filterQuery as FilterQuery;
        const sort = queryParts.sort as SortBy;
        const originalQuery = queryParts.originalQuery;
        const outOfStock = !!queryParts.outOfStock;
        const noInStockResults = Boolean(queryParts.noInStockResults);
        return {
            ...queryParts,
            pageIndex,
            showAll,
            filterQuery,
            sort,
            originalQuery,
            outOfStock,
            noInStockResults,
        };
    }, [router.asPath]);

    const {
        pageIndex,
        showAll,
        filterQuery = {},
        sort = {},
        originalQuery,
        outOfStock,
        noInStockResults,
        ...additionalParams
    } = searchParams;

    const { categorySortByOptions, defaultSortByOption } = useSortByOptions(
        variant,
        outOfStock,
        type
    );

    // memoized Filter Query
    const filterQueryString = useMemo(() => {
        return JSON.stringify(filterQuery);
    }, [router.asPath]);

    // Memoized Base Query
    const baseQueryString = useMemo(() => {
        return JSON.stringify(baseQuery);
    }, [router.asPath, baseQuery]);

    // when a page is fetched the page number is stored here
    // we use this to calculate the starting and ending rows to fetch
    const [lastFetchedPage, setLastFetchedPage] = useState<number>(0);

    // memoizedSort
    const sortString = useMemo(() => {
        return JSON.stringify(sort);
    }, [router.asPath]);

    const { getListingCards, getQuery } = (() => {
        switch (variant) {
            case ListingPageVariant.MODEL:
                return modelListingHelper(onCreateAlert);

            case ListingPageVariant.PRODUCT:
                return productListingHelper(formatPrice, showProductNames);
        }
    })();

    const shouldRequestOutOfStockRecommendations = Boolean(
        outOfStockRecommendationsFlag?.isEnabled && noInStockResults
    );

    const outOfStockRecommendations = useSimilarModelRecommendations(
        getModelsIdsFromSearchResults(),
        activeMarket.code,
        {
            enabled: shouldRequestOutOfStockRecommendations,
        }
    );

    const defaultSortOption = getDefaultSortOption
        ? getDefaultSortOption(categorySortByOptions)
        : defaultSortByOption;

    function getModelsIdsFromSearchResults() {
        return searchResults.results
            .map(({ modelId }) => Number(modelId.values[0]))
            .filter((modelId) => !isNaN(modelId));
    }

    function renderOutOfStockRecommendations() {
        if (!outOfStockRecommendationsFlag) {
            return null;
        }

        if (
            outOfStockRecommendationsFlag.variant?.name ===
                RecommendationsVariant.VARIANT &&
            outOfStockRecommendations.isFetching
        ) {
            return <ModelRecommendationsCarouselSkeleton />;
        }

        if (outOfStockRecommendations.data) {
            return (
                <ModelRecommendationsCarousel
                    title={t('Similar products')}
                    pageModelIds={getModelsIdsFromSearchResults()}
                    modelRecommendations={{
                        ...outOfStockRecommendations.data,
                        ...outOfStockRecommendationsFlag,
                    }}
                />
            );
        }

        return null;
    }

    /**
     * Fetch and append the search results to the currently fetched search results
     */
    async function appendResults() {
        const page = pageIndex + 1;
        const start = lastFetchedPage * resultsPerPage;
        const rows = getNumRows({
            page,
            lastFetchedPage,
            totalResults: searchResults.totalResults,
            resultsPerPage,
            isViewMore,
        });

        const searchResultsResponse = await getResults(rows, start);

        if (!searchResultsResponse) return;

        const appendedResults = {
            ...searchResultsResponse,
            results: [
                ...searchResults.results,
                ...searchResultsResponse.results,
            ],
            resultCount:
                searchResultsResponse.resultCount + searchResults.resultCount,
        };

        if (isMounted()) {
            setSearchResults(appendedResults);
        }
    }

    async function replaceResults(start: number, rows: number) {
        setSearchResults({
            ...searchResults,
            ...nullSearchResults,
        });

        const searchResultsResponse = await getResults(rows, start);

        if (!searchResultsResponse) return;

        if (isMounted()) {
            const sortByOption = transformSortByToSortByOption({
                sort,
                sortOptions: categorySortByOptions,
                defaultSortOption: defaultSortOption as unknown as SortByOption,
            });

            setSearchResults(searchResultsResponse);

            const searchFilters = {
                ...(baseQuery.filterQuery ?? {}),
                ...filterQuery,
            };

            addSearchResultsToDataLayer({
                searchResults: searchResultsResponse.results,
                pageType: type,
                searchVariant: variant,
                searchFilters,
                sortedBy: sortByOption.value,
            });
        }
    }

    async function getSkuForResult(
        baseQuery: Query,
        outOfStock: boolean
    ): Promise<SearchValue> {
        const productSKUQuery = getProductSKUQuery(
            baseQuery,
            activeMarket.code,
            outOfStock
        );

        const searchResponse: QueryResponse<SearchListingProduct> =
            await getListingResults(
                productSKUQuery,
                activeMarket.iso,
                activeMarket.code
            );

        const result = searchResponse.results[0];
        const sku = result.productSku;

        return sku;
    }

    async function getResults(
        rows: number,
        start: number
    ): Promise<SearchResultsType | void> {
        // Transforms the sort by value stored in the url to be usable by the listing query
        const sortByOption = transformSortByToSortByOption({
            sort,
            sortOptions: categorySortByOptions,
            defaultSortOption: defaultSortOption as unknown as SortByOption,
        });
        const sortBy = getListingQuerySortBy(
            sortByOption,
            isProductListingPage
        );

        setNumResultSkeletons(rows);

        const requiredFacets = [
            'model_brand',
            'model_category',
            'model_product_type',
            'product_condition_star_rating',
            'product_price',
        ].filter((facet) => !disallowedFilters.includes(toCamelCase(facet)));

        const searchFacets = [...requiredFacets, '*'];

        // Grab results
        const searchResponse: QueryResponse<
            SearchListingProduct & SearchListingModel
        > = await getListingResults(
            getQuery(
                baseQuery,
                start,
                rows,
                filterQuery,
                sortBy,
                activeMarket.code,
                searchFacets,
                outOfStock
            ),
            activeMarket.iso,
            activeMarket.code
        );

        const suggestion = searchResponse.spellcheck?.suggestions?.[0];

        if (
            baseQuery.query &&
            baseQuery.query !== originalQuery &&
            suggestion &&
            suggestion !== originalQuery
        ) {
            return setSearchParams(
                router,
                {
                    q: suggestion,
                    originalQuery: baseQuery.query,
                },
                false
            );
        }

        if (
            isAutoOutOfStockSearchEnabled &&
            type === ListingPageType.SEARCH &&
            !Object.keys(filterQuery).length &&
            searchResponse.results.length === 0 &&
            !outOfStock
        ) {
            return setSearchParams(
                router,
                {
                    ...router.query,
                    outOfStock: 'true',
                    noInStockResults: 'true',
                },
                false
            );
        }

        const hasOneProductInResult =
            searchResponse.results.length === 1 &&
            parseInt(
                searchResponse.results[0]?.productPriceCount?.values?.[0] ?? ''
            ) === 1;

        /**
         * This is specifically for the case that a customer searches by sku - and we
         * want to provide a direct link to the product.
         *
         * In the future we might want to extend this behaviour to all models with a
         * single product listed?
         */
        if (isModelListingPage && hasOneProductInResult) {
            const sku = await getSkuForResult(baseQuery, outOfStock);

            searchResponse.results[0].productSku = sku;
        }

        const { facets, ...searchResultsResponse } = searchResponse;

        if (isMounted()) {
            const page = pageIndex + 1;
            setFacets(facets);
            if (!facetsFetched) setFacetsFetched(true);
            setLastFetchedPage(page);
            setNumResultSkeletons(0);
        }

        return { sort, ...searchResultsResponse };
    }

    function getStart() {
        if (isViewMore) return 0;

        return pageIndex * resultsPerPage;
    }

    function handleSearchResultsUpdate() {
        if (!validateBaseQuery(baseQuery)) {
            return;
        }

        const hasSameSearchParams = (() => {
            if (!searchResults.sort) return true;

            return fastDeepEqual(searchResults.sort, searchParams.sort || {});
        })();

        // If showAll is selected we lazily load and then append
        // results as user scrolls.
        const shouldAppendResults =
            (showAll || isViewMore) && pageIndex !== 0 && hasSameSearchParams;

        if (shouldAppendResults) {
            appendResults();
            return;
        }

        const page = pageIndex + 1;
        const lastFetchedPage = 0;

        const start = getStart();
        const rows = getNumRows({
            page,
            lastFetchedPage,
            totalResults: searchResults.totalResults,
            resultsPerPage,
            isViewMore,
        });

        replaceResults(start, rows);
    }

    const triggerRef = useSearchResultsInfiniteScroll({
        showAll,
        totalResults: searchResults.totalResults,
        resultsPerPage,
        pageIndex,
    });

    /**
     * A map of facet transforms, keyed by {SearchFacet} and accepting the value
     * for a particular facet field. Returning a label for that value
     *
     * @returns {FacetOptionTransformMap}
     */
    const facetLabelTransforms: FacetOptionTransformMap = {
        productConditionStarRating: (value) => {
            // @ts-ignore - Element implicitly has an any type - Touch and Change
            return translatedStarRatings[value];
        },
        productPrice: (value) => {
            value = transformFacetsOptionValue({ value, t });

            return value.replace(/\d+/g, (match: string) => {
                return formatPrice(centToDecimal(parseInt(match)), {
                    trailingZeros: false,
                });
            });
        },
        modelYearOfReleaseModelSpecificationNum: (value) => {
            const beforeLanguage = (value: string) =>
                t('Before {{value}}', {
                    value,
                    context: 'search-filters',
                });
            const afterLanguage = (value: string) =>
                t('After {{value}}', {
                    value,
                    context: 'search-filters',
                });

            return transformFacetsOptionValue({
                value,
                bottomBoundaryLanguage: beforeLanguage,
                topBoundaryLanguage: afterLanguage,
                t,
            });
        },
        productPriceModifierIds: (value) => {
            const modifiers = modifierGroups?.flatMap(
                ({ modifiers }) => modifiers
            );
            const modifier = modifiers?.find(
                (modifier) => `${modifier.id}` === value
            );

            return modifier?.name ?? '';
        },
    };

    /**
     * A Handler that is called when sort by is updated
     *
     * @param {SortByOption} sortByOption The new sort by option
     * @returns {void}
     */
    function handleSortByChange(sortByOption: SortByOption) {
        // If the sortByOption is the default sort option then remove it from the url
        const sort =
            sortByOption === defaultSortOption
                ? {}
                : {
                      [sortByOption.facet]: sortByOption.direction,
                  };

        const pageReset = showAll ? { page: undefined } : {};

        setSearchParams(router, {
            sort,
            ...pageReset,
        });
    }

    /**
     * Handler for showing in stock models
     */
    function handleBackToInStockModels() {
        scrollToTop();

        // if we don't have a url saved to restore then we just remove the out of stock param
        // this happens if we come back to this page from a saved url
        if (!inStockModelsURL.current) {
            setSearchParams(router, {
                page: undefined,
                showAll: undefined,
                filterQuery: undefined,
                sort: undefined,
                originalQuery: undefined,
                outOfStock: undefined,
            });
            return;
        }

        // restore our saved url
        router.push(
            {
                pathname: inStockModelsURL.current.pathname,
                query: inStockModelsURL.current.query,
            },
            inStockModelsURL.current.asPath
        );
    }

    /**
     * Handler for showing the out of stock models.
     */
    function handleShowOutOfStockModels() {
        // first we store the current url so we can later restore it
        inStockModelsURL.current = getCurrentRoute(router);

        // then we clear our search params, setting show out of stock
        setSearchParams(router, {
            page: undefined,
            showAll: undefined,
            filterQuery: undefined,
            sort: undefined,
            originalQuery: undefined,
            outOfStock: true,
        });

        scrollToTop();
    }

    function getFilterOptionLabel(value: string, facetId: SearchFacet) {
        const transformer = facetLabelTransforms[facetId];

        if (transformer) {
            return transformer(value);
        }

        if (value === 'true') {
            return t('Yes');
        }

        if (value === 'false') {
            return t('No');
        }

        return null;
    }

    /**
     * Transform a filter query to an array of filter options
     *
     * @param {string} facetId The facet that we want to extract filter options for
     * @param {FilterQuery} filterQuery The filterQuery we are extracting from
     */
    function transformFilterQueryToOptions(
        facetId: SearchFacet,
        filterQuery: FilterQuery
    ): FilterOption[] {
        if (!filterQuery?.[facetId]) {
            return [];
        }
        const filter = filterQuery[facetId];
        const options = Array.isArray(filter) ? filter : [filter];

        return options.map((option) => {
            const value = option.toString();
            return {
                label: getFilterOptionLabel(value, facetId) ?? value,
                value,
            };
        });
    }

    /**
     * Transforms an array of facets into an array of filters
     *
     * @param {Facet[]} facets An array of facets to be transformed
     * @param {SearchParam} filterQuery the filter query search params
     * @returns {Filter[]} The given facets, transformed into Filters
     */
    function transformFacetsToFilters(
        facets: Facet[],
        filterQuery: FilterQuery
    ): Filter[] {
        const facetDenyList: SearchFacet[] = [];
        const facetAlwaysAllowList: string[] = [];
        if (!outOfStock) {
            facetAlwaysAllowList.push(
                ...[
                    SearchFacet.productPrice,
                    SearchFacet.productConditionStarRating,
                    SearchFacet.productPriceModifier,
                ]
            );
        }
        facetAlwaysAllowList.push(
            ...Object.keys(filterQuery) // Whitelist already applied facets
        );

        if (!isProductListingPage) {
            facetDenyList.push(SearchFacet.productPriceModifier);
        }

        return facets
            .filter((facet) => {
                const id = toCamelCase(facet.field);

                if (facetDenyList.includes(id as SearchFacet)) {
                    return false;
                }

                // Ignore whitelist
                if (facetAlwaysAllowList.includes(id)) {
                    return true;
                }

                // Remove facet if less than two values
                return (
                    facet.values.filter(
                        (value: FacetValue) => parseInt(value.count) > 0
                    ).length >= 2
                );
            })
            .map((facet): Filter => {
                const id = toCamelCase<SearchFacet>(facet.field);
                const label = translatedFacetTitles?.[id] || id;
                const showCount = shouldShowFacetCount({ id, variant });
                const isPriceFilter = id === SearchFacet.productPrice;
                const isYearOfRelease =
                    id === SearchFacet.modelYearOfReleaseModelSpecificationNum;
                const isNumberFacet = id.endsWith('Num');

                let options: FilterOption[] = facet.values
                    .filter(
                        (value) =>
                            isPriceFilter ||
                            isNumberFacet ||
                            parseInt(value.count) > 0
                    )
                    .map((option) => {
                        const quantity = parseInt(option.count);
                        const label = getFilterOptionLabel(
                            option.fieldValue,
                            id
                        );

                        if (label) {
                            return {
                                label,
                                value: option.fieldValue,
                                quantity,
                            };
                        }

                        const value = transformFacetsOptionValue({
                            value: option.fieldValue,
                            t,
                        });

                        return {
                            label: isNumberFacet
                                ? formatSearchFacetNumber(
                                      value,
                                      activeMarket.slug
                                  )
                                : value,
                            value: option.fieldValue,
                            quantity,
                        };
                    });

                if (isNumberFacet || isPriceFilter) {
                    options = trimFilterOptions(options);
                }

                if (isYearOfRelease) {
                    // Solr can't handle Year of Release being sent to the client in reserve order
                    options.reverse();
                }

                const selectedOptions = transformFilterQueryToOptions(
                    id,
                    filterQuery
                );

                return {
                    id,
                    label,
                    options,
                    selectedOptions,
                    // @ts-ignore - Element implicitly has an any type - Touch and Change
                    selectedVariant: additionalParams[id],
                    displayOptionQuantity: showCount,
                    baseFilterQuery: baseQuery.filterQuery ?? {},
                    getAllOptions: () => getFacetOptions(id),
                };
            });
    }

    /**
     * Handler for when we want to update the search filter
     *
     * @param filters Filters to be used as search params
     */
    function handleSearchFilterUpdate(filters: Filter[]): void {
        setActiveFacetData(undefined);
        setSearchParams(router, {
            ...transformFiltersToSearchParams(filters),
            page: undefined,
        });
    }

    /**
     * Load the search results for the given page.
     *
     * @param pageIndex the page index to load
     */
    function handlePaginationUpdate(pageIndex: number) {
        const page = pageIndex === 0 ? undefined : pageIndex + 1;
        const pushHistory = !isViewMore;
        const shouldScrollToTop = !isViewMore;

        setSearchParams(router, { page }, pushHistory);

        if (shouldScrollToTop) {
            scrollToTop();
        }
    }

    /**
     * Handler for when we want to show all results rather than providing
     * buttoned pagination
     */
    function handleShowAllResults(): void {
        setSearchParams(router, {
            showAll: true,
            page: undefined,
        });
        setSearchResults(nullSearchResults);
    }

    /**
     * Handles updating the user's search parameter to their original query
     * and resetting the result set back to page 1.
     */
    function handlePerformOriginalSearch(): void {
        setSearchParams(router, {
            q: originalQuery?.toString(),
            page: undefined,
        });
    }

    /**
     * Returns the result count for a given search results object
     *
     * @param {QueryResponse} searchResults The search results we want to return a count for
     * @returns {number} the result count
     */
    function getResultCount(
        searchResults: Omit<
            QueryResponse<SearchListingModel | SearchListingProduct>,
            'facets'
        >
    ): number {
        return searchResults.totalResults;
    }

    function handleFilterOpen(id: string): Promise<void> {
        return new Promise(async (resolve: any) => {
            const facetFilterQuery = { ...filterQuery };
            delete facetFilterQuery[id];

            // Remove options initially
            setFacets(
                facets.map((facet: Facet) => {
                    return facet.field === id
                        ? {
                              ...facet,
                              values: [],
                          }
                        : facet;
                })
            );

            // Fetch count info
            const { facets: updatedFacets } = await getListingResults(
                getQuery(
                    baseQuery,
                    1,
                    0,
                    facetFilterQuery,
                    sort,
                    activeMarket.code,
                    [toSnakeCase(id)]
                ),
                activeMarket.iso,
                activeMarket.code
            );

            setActiveFacetData(updatedFacets[0]);

            resolve();
        });
    }

    async function getFacetOptions(id: string) {
        const { facets } = await getListingResults(
            getQuery(baseQuery, 1, 0, {}, sort, activeMarket.code, [
                toSnakeCase(id),
            ]),
            activeMarket.iso,
            activeMarket.code
        );

        return facets?.[0]?.values.map(({ fieldValue }) => fieldValue) ?? [];
    }

    /**
     * Calculates amount of active filters
     *
     * @param filters Filters to be used as search params
     */
    function getFiltersAppliedCount(filters: Filter[]) {
        return filters.reduce((arr, cur) => {
            return arr + cur.selectedOptions.length;
        }, 0);
    }

    const filters = useMemo(() => {
        const facetsToTransform = facets.map((facet: Facet) => {
            const activeFacetId = activeFacetData?.field;
            return activeFacetData &&
                activeFacetId &&
                facet.field === activeFacetId
                ? activeFacetData
                : facet;
        });

        const transformedFilters = transformFacetsToFilters(
            facetsToTransform,
            filterQuery
        );

        const filters = disallowedFilters
            ? transformedFilters.filter(
                  (filter) => !disallowedFilters.includes(filter.id)
              )
            : transformedFilters;

        // We only want to show 10 filters at once
        return filters.slice(0, 10);
    }, [facets, filterQueryString, activeFacetData, modifierGroups]);

    // Fetch search results if our category or url search params change
    useEffect(() => {
        handleSearchResultsUpdate();
    }, [
        pageIndex,
        filterQueryString,
        showAll,
        sortString,
        baseQueryString,
        outOfStock,
    ]);

    const contentfulTileOffset =
        showAll || isViewMore ? 0 : pageIndex * resultsPerPage;

    // @ts-ignore - the getListingCards function takes different searchResults types depending on the page variant
    const listingCards = getListingCards(searchResults, numResultSkeletons);
    const isLoadingCards = searchResults.totalResults === -1;

    const { items: contentfulTiles, insertContentfulTiles } =
        useContentfulTiles(isLoadingCards);

    const resultsInView = isViewMore
        ? listingCards
        : insertContentfulTiles(
              listingCards,
              contentfulTiles,
              contentfulTileOffset
          );
    const resultsInViewCount = listingCards.length;

    const renderSmallTabletFilters = () => {
        return (
            <Fragment>
                <div
                    className="small-tablet-filters"
                    data-testid={`${testId}__filters__small-tablet`}
                >
                    <SearchFilterGroupMobile
                        isOpen={mobileSearchFilterOpen}
                        resultsCount={getResultCount(searchResults)}
                        filters={filters}
                        onFilterOpen={handleFilterOpen}
                        onApply={(selectedFilters) => {
                            handleSearchFilterUpdate(selectedFilters);
                            setSearchFilterGroupMobileOpen(false);
                        }}
                        onClose={() => {
                            setSearchFilterGroupMobileOpen(false);
                        }}
                        onCancel={() => {
                            setSearchFilterGroupMobileOpen(false);
                        }}
                    />
                </div>
                <div className="control-group">
                    <ControlGroupSkeleton
                        data-testid={`${testId}__control-group-skeleton`}
                        className="control-group-skeleton"
                    />
                    <ControlGroup
                        data-testid={`${testId}__control-group`}
                        className="control-group-content"
                        options={categorySortByOptions}
                        selectedOption={transformSortByToSortByOption({
                            sort,
                            sortOptions: categorySortByOptions,
                            defaultSortOption:
                                defaultSortOption as unknown as SortByOption,
                        })}
                        onSortByChange={handleSortByChange}
                        setSearchFilterGroupMobileOpen={
                            setSearchFilterGroupMobileOpen
                        }
                        filtersAppliedCount={getFiltersAppliedCount(filters)}
                    />
                </div>
            </Fragment>
        );
    };

    const renderDesktopFilters = () => {
        const wrapperProps = {
            as: 'section' as React.ElementType,
            className: 'desktop-filters',
            'data-testid': `${testId}__filters__desktop`,
        };

        return (
            <Fragment>
                <ConstrainedWrapper {...wrapperProps}>
                    <Fragment>
                        <FilterResultsSummary
                            filters={filters}
                            onUpdate={handleSearchFilterUpdate}
                            data-testid={`${testId}__filter-summary`}
                        />
                        {facetsFetched ? (
                            <SearchFilterGroup
                                filters={filters}
                                data-testid={`${testId}__filters`}
                                onUpdate={handleSearchFilterUpdate}
                                onFilterOpen={handleFilterOpen}
                            />
                        ) : (
                            <SearchFilterGroupSkeleton
                                numFilters={getNumFilterSkeletons({
                                    variant,
                                    showProductNames,
                                })}
                            />
                        )}
                    </Fragment>
                </ConstrainedWrapper>
                <Divider className="divider" />
            </Fragment>
        );
    };

    return (
        <StyledListingPage
            isLoading={isLoading}
            data-testid={testId}
            {...otherProps}
        >
            {baseQuery.query &&
                originalQuery &&
                baseQuery.query !== originalQuery && (
                    <ConstrainedWrapper
                        data-testid={`${testId}__suggested-spelling`}
                        className="spelling-suggestion-notice"
                    >
                        <p>
                            <Trans
                                components={[
                                    <Link
                                        key="0"
                                        onClick={handlePerformOriginalSearch}
                                    >
                                        0
                                    </Link>,
                                ]}
                            >
                                {t(
                                    'Showing results for <strong>{{suggestedTerm}}</strong>. Search instead for <0>{{originalTerm}}</0>.',
                                    {
                                        suggestedTerm: baseQuery.query,
                                        originalTerm: originalQuery,
                                    }
                                )}
                            </Trans>
                        </p>
                    </ConstrainedWrapper>
                )}

            {renderSmallTabletFilters()}
            {renderDesktopFilters()}

            <SearchResults
                isLoading={isLoading}
                paginationVariant={selectedPaginationVariant}
                resultsInView={resultsInView}
                resultsInViewCount={resultsInViewCount}
                displayAllResults={showAll}
                totalResultsCount={searchResults.totalResults}
                sortBy={transformSortByToSortByOption({
                    sort,
                    sortOptions: categorySortByOptions,
                    defaultSortOption:
                        defaultSortOption as unknown as SortByOption,
                })}
                page={pageIndex}
                variant={searchResultsVariant}
                onCreateAlert={onCreateAlert}
                onPaginationClick={handlePaginationUpdate}
                onSeeAllClick={handleShowAllResults}
                sortByOptions={categorySortByOptions}
                onSortByChange={handleSortByChange}
                data-testid={`${testId}__search-results`}
                resultsPerPage={resultsPerPage}
                onBackToInStockModels={handleBackToInStockModels}
                onFindOutOfStockModels={handleShowOutOfStockModels}
                displayOutOfStock={outOfStock}
                noInStockResults={noInStockResults}
            />
            <div ref={triggerRef} />
            {renderOutOfStockRecommendations()}
        </StyledListingPage>
    );
};
